import React, { useState, useMemo } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalculateIcon from '@mui/icons-material/Calculate';
import InfoIcon from '@mui/icons-material/Info';
import InputIcon from '@mui/icons-material/Input';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';

export interface NivelPresionAnalisisEchometerProps {
    liquidLevel?: any;
    variablesToUpdate?: any;
    onChangeLiquidLevel?: (value: number, label: string) => void;
    /** When true (compact, 50% panel): show Input/Calculo as tabs. When false (extended, 100%): show both side by side. */
    isCompact?: boolean;
}

const configFields = [
    { label: 'Nivel de Líquido (ft)', name: 'liquidLevel' as const },
    { label: 'Prof. Bomba (ft)', name: 'pumpDepth' as const },
    { label: 'Prof. Formación (ft)', name: 'tvd' as const },
    { label: 'CHP (psi)', name: 'chp' as const },
    { label: 'THP (psi)', name: 'thp' as const },
    { label: 'Gradiente (psi/ft)', name: 'fluidGradient' as const, step: '0.01' },
];

export const NivelPresionAnalisisEchometer = ({
    liquidLevel,
    variablesToUpdate,
    onChangeLiquidLevel = () => {},
    isCompact = true,
}: NivelPresionAnalisisEchometerProps) => {
    const [params, setParams] = useState({
        tvd: 5000,
        pumpDepth: 4500,
        liquidLevel: 3142,
        chp: 10,
        thp: 10,
        fluidGradient: 0.35,
    });
    const [echometerTab, setEchometerTab] = useState<'input' | 'calculo'>('input');

    const calculations = useMemo(() => {
        const submergence = params.pumpDepth - params.liquidLevel;
        const pip = params.chp + submergence * params.fluidGradient;
        const pbhp = pip + (params.tvd - params.pumpDepth) * params.fluidGradient;
        return {
            submergence: parseFloat(submergence.toFixed(0)),
            pip: parseFloat(pip.toFixed(0)),
            pbhp: parseFloat(pbhp.toFixed(0)),
            percentOverPump: parseFloat(((submergence / params.pumpDepth) * 100).toFixed(0)),
        };
    }, [params]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const key = name as keyof typeof params;
        const num = parseFloat(value) || 0;
        setParams((prev) => ({ ...prev, [key]: num }));
    };

    const fluidTopPos = (params.liquidLevel / params.tvd) * 100;
    const pumpPos = (params.pumpDepth / params.tvd) * 100;

    const inputSection = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <SettingsIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                    Configuración Técnica
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {configFields.map((field) => (
                    <Box key={field.name}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                            {field.label}
                        </Typography>
                        <TextField
                            size="small"
                            type="number"
                            name={field.name}
                            value={params[field.name]}
                            onChange={handleInputChange}
                            inputProps={{ step: field.step || '1' }}
                            fullWidth
                            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
                        />
                    </Box>
                ))}
            </Box>
            <Paper elevation={0} sx={{ mt: 0.5, p: 1.5, bgcolor: 'white', border: '1px solid', borderColor: 'grey.200' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalculateIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        Estado de Operación
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 0.5, borderBottom: '1px solid', borderColor: 'grey.200' }}>
                        <Typography component="span" variant="caption" color="text.secondary">Sumergencia:</Typography>
                        <Typography component="span" variant="caption" fontWeight={700} color="primary.main">{calculations.submergence} ft</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 0.5, borderBottom: '1px solid', borderColor: 'grey.200' }}>
                        <Typography component="span" variant="caption" color="text.secondary">PIP Est.:</Typography>
                        <Typography component="span" variant="caption" fontWeight={700}>{calculations.pip} psi</Typography>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );

    const calculoSection = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', minHeight: 0 }}>
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', overflow: 'hidden' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell colSpan={2} sx={{ borderBottom: '1px solid', borderColor: 'grey.200', py: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <WaterDropIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                                <Typography variant="caption" fontWeight={700} color="text.secondary">
                                    Resultados del Análisis
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[
                            { label: 'CHP:', value: params.chp, unit: 'psi' },
                            { label: 'THP:', value: params.thp, unit: 'psi' },
                            { label: '% Líquido sobre bomba:', value: calculations.percentOverPump, unit: '%' },
                            { label: 'Fluido sobre la bomba:', value: calculations.submergence, unit: 'ft' },
                            { label: 'Fluido sobre la bomba (gas free):', value: (calculations.submergence * 0.8).toFixed(0), unit: 'ft' },
                            { label: 'Prof. Bomba:', value: params.pumpDepth, unit: 'ft' },
                            { label: 'Prof. Formación:', value: params.tvd, unit: 'ft' },
                            { label: 'PIP:', value: calculations.pip, unit: 'psi' },
                            { label: 'PBHP:', value: calculations.pbhp, unit: 'psi' },
                        ].map((row, idx) => (
                            <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                                <TableCell sx={{ py: 0.75, fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>{row.label}</TableCell>
                                <TableCell sx={{ py: 0.75, fontSize: 11, fontWeight: 700, textAlign: 'right' }}>
                                    {row.value}{' '}
                                    <Typography component="span" variant="caption" color="text.secondary" fontWeight={400}>
                                        {row.unit}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
            <Box
                sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'primary.light',
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    borderRadius: 1,
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'flex-start',
                }}
            >
                <Box sx={{ bgcolor: 'primary.main', p: 0.75, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <InfoIcon sx={{ fontSize: 12, color: 'white' }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={700} color="primary.dark" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.25 }}>
                        Interpretación Automática
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: 10 }}>
                        El pozo se encuentra produciendo con una sumergencia de {calculations.submergence} ft. El nivel de fluido se mantiene estable bajo los
                        parámetros actuales de bombeo. Se recomienda monitorear PIP si la presión baja de 50 psi.
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                bgcolor: 'white',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    px: 1.5,
                    py: 0.75,
                    bgcolor: 'grey.50',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ width: 3, height: 16, bgcolor: 'primary.main', borderRadius: 2 }} />
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ fontSize: 11 }}>
                    Análisis de Nivel y Presión (Echometer TAM)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                    <CalendarMonthIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                        10 - Enero - 2025
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
                {/* Well diagram - always left, fixed width */}
                <Box
                    sx={{
                        width: 140,
                        flexShrink: 0,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        bgcolor: 'grey.50',
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ fontSize: 9 }}>Gun - WG Unkno</Typography>
                        <KeyboardArrowDownIcon sx={{ fontSize: 12, color: 'primary.main' }} />
                    </Box>
                    <Box
                        sx={{
                            position: 'relative',
                            width: 80,
                            flex: 1,
                            minHeight: 180,
                            borderLeft: '2px solid',
                            borderRight: '2px solid',
                            borderColor: 'grey.400',
                            bgcolor: 'white',
                        }}
                    >
                        <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 0, width: 2, height: '100%', bgcolor: 'grey.300' }} />
                        {[...Array(6)].map((_, i) => (
                            <Box key={i} sx={{ position: 'absolute', top: `${i * 20}%`, left: -28, width: 140, borderTop: '1px solid', borderColor: 'grey.200' }}>
                                <Typography variant="caption" sx={{ position: 'absolute', left: -22, top: '50%', transform: 'translateY(-50%)', fontSize: 9, color: 'grey.500' }}>
                                    {i * 1000}
                                </Typography>
                            </Box>
                        ))}
                        <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', zIndex: 4, top: `${pumpPos}%`, transition: 'top 0.7s ease-in-out' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Box component="svg" sx={{ width: 20, height: 20, color: 'grey.800' }} viewBox="0 0 24 24" fill="none">
                                    <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth={2.5} />
                                    <line x1="20" y1="4" x2="4" y2="20" stroke="currentColor" strokeWidth={2.5} />
                                </Box>
                                <Paper elevation={1} sx={{ position: 'absolute', right: -72, bgcolor: 'grey.800', color: 'white', fontSize: 9, px: 0.75, py: 0.25, fontWeight: 700 }}>
                                    PUMP @ {params.pumpDepth} ft
                                </Paper>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '100%',
                                bgcolor: 'rgba(33, 150, 243, 0.15)',
                                borderTop: '1px solid',
                                borderColor: 'primary.main',
                                height: `${100 - fluidTopPos}%`,
                                transition: 'height 0.7s ease-in-out',
                            }}
                        >
                            <Box sx={{ position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.25, px: 0.25, opacity: 0.4 }}>
                                {[...Array(16)].map((_, i) => (
                                    <Box key={i} sx={{ width: 4, height: 4, bgcolor: 'primary.main', borderRadius: '50%', animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 0.4 }, '50%': { opacity: 1 } } }} />
                                ))}
                            </Box>
                            <Box sx={{ position: 'absolute', top: -8, left: 0, width: '100%', display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ flex: 1, borderTop: '1px dashed', borderColor: 'primary.main' }} />
                                <Paper elevation={1} sx={{ position: 'absolute', left: -75, bgcolor: 'primary.main', color: 'white', fontSize: 9, px: 0.75, py: 0.25, fontWeight: 700 }}>
                                    NIVEL {params.liquidLevel} ft
                                </Paper>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Right: Input + Calculo - tabs in compact, side-by-side in extended */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {isCompact ? (
                        <>
                            <Box sx={{ flexShrink: 0, px: 1, pt: 0.5, pb: 0 }}>
                                <ToggleButtonGroup
                                    size="small"
                                    value={echometerTab}
                                    exclusive
                                    onChange={(_, v) => v && setEchometerTab(v)}
                                    aria-label="Input o Calculo"
                                    sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: 11 } }}
                                >
                                    <ToggleButton value="input">
                                        <InputIcon sx={{ fontSize: 12, mr: 0.25 }} /> Input
                                    </ToggleButton>
                                    <ToggleButton value="calculo">
                                        <CalculateOutlinedIcon sx={{ fontSize: 12, mr: 0.25 }} /> Calculo
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                                {echometerTab === 'input' ? inputSection : calculoSection}
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ flex: 1, display: 'flex', gap: 2, p: 2, overflow: 'hidden', minHeight: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto' }}>{inputSection}</Box>
                            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto' }}>{calculoSection}</Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
